import { useApp } from '@/App';
import FleetModal from '@/libs/antd/components/Modal';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import {
	Pipeline,
	ProspectSteps,
	getPipelineDetail,
	savePipelineFBDH,
	updatePipelineFBDH,
} from '@/libs/fleet-api/services/pipeline';
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Checkbox,
	Col,
	Divider,
	Flex,
	Form,
	Input,
	notification,
	Result,
	Row,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { FC, Fragment, useState } from 'react';
import PipelineMainInformation from '../../../components/pipeline/PipelineMainInformation';
import fetcher from '@/libs/fleet-api/fetcher';
import FleetTitle from '@/libs/antd/components/Title';
import DataRow from '@/libs/antd/components/DataRow';
import { formatCurrency } from '@/libs/antd/formater';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type FBDHPipelinePageProps = {
	pipelineId: number;
};

const FBDHPipelinePage: FC<FBDHPipelinePageProps> = ({ pipelineId }) => {
	const { navigate } = useApp();

	const [isSavingMode, setIsSavingMode] = useState<boolean>(false);
	const [successUpdateModal, setSuccessUpdateModal] = useState<boolean>(false);
	const [successSaveModal, setSuccessSaveModal] = useState<boolean>(false);

	const { form, fieldName } = useForm<Pipeline<'payload'>>();

	const updateFBDHPipelineMutation = useMutation({
		mutationFn: updatePipelineFBDH,
	});

	const saveFBDHPipelineMutation = useMutation({
		mutationFn: savePipelineFBDH,
	});

	const { data, isLoading, refetch, isError } = useQuery({
		queryKey: ['getPipelineDetail', pipelineId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getPipelineDetail(id);
		},
		enabled: !!pipelineId,
	});

	if (!data) return null;

	if (!data.data.pipelineDetail) return <h2>No Pipeline Data</h2>;

	const { pipelineDetail, customerDocuments, prospectInformation } = data.data;

	const selectedPlafons =
		prospectInformation?.financingApplicationLimitInformation.filter(
			(plafon) => {
				if (plafon.plafonType) {
					return pipelineDetail.pipelinePlafonTypeIds.includes(
						plafon.plafonType.id,
					);
				}
				return false;
			},
		);

	const onUpdate = async () => {
		try {
			await updateFBDHPipelineMutation.mutateAsync({
				...form.getFieldsValue(),
				id: pipelineId,
				prospectId: pipelineDetail.prospectId,
			});
			setSuccessUpdateModal(true);
			refetch();
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	const onSubmit = async (values: Pipeline<'payload'>) => {
		try {
			await saveFBDHPipelineMutation.mutateAsync({
				...values,
				prospectId: pipelineDetail.prospectId,
				id: pipelineDetail.pipelineHotProspectId,
			});
			setSuccessSaveModal(true);
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	if (isError) return <GenericErrorMessage />;

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetModal
				open={successUpdateModal}
				okText="NEXT"
				onOk={() => {
					setSuccessUpdateModal(false);
				}}
				onCancel={() => setSuccessUpdateModal(false)}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses Mengupdate</div>}
				/>
			</FleetModal>
			<FleetModal
				open={successSaveModal}
				okText="NEXT"
				onOk={() => {
					setSuccessSaveModal(false);
					navigate('FBDHPipelineListPage');
				}}
				onCancel={() => {
					setSuccessSaveModal(false);
					navigate('FBDHPipelineListPage');
				}}
				cancelButtonProps={{ style: { display: 'none' } }}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses Menyimpan</div>}
				/>
			</FleetModal>
			<PipelineMainInformation
				mainInformation={{
					prospectId: pipelineDetail.prospectId,
					prospectStatus: pipelineDetail?.prospectStatus.value,
					orderSource: pipelineDetail?.orderSource?.value ?? '-',
					customerId: pipelineDetail?.customerId,
					customerName: pipelineDetail?.customerName,
					customerGroup: pipelineDetail?.customerGroup ?? { id: 0, value: '' },
					industrySector: pipelineDetail?.industrySector ?? {
						id: 0,
						value: '',
					},
					napNo: pipelineDetail?.napNo,
					slikResult: '',
					frm: { id: 0, value: '' },
					creditAnalyst: { id: 0, value: '' },
					existingPlafond: pipelineDetail?.existingFinancingLimit ?? 0,
					appliedPlafond: pipelineDetail?.limitApplication ?? 0,
					applicationStatus: pipelineDetail?.prospectStatus.value,
					newOrderDate: pipelineDetail?.newOrderDate,
					outstandingPrincipal: pipelineDetail?.outstandingPrincipal ?? 0,
				}}
				customerBusinessDocuments={customerDocuments.customerBusinessDocuments}
				customerLegalDocuments={customerDocuments.customerLegalDocuments}
				customerReferralDocuments={customerDocuments.customerReferralDocuments}
				isLoading={isLoading}
				prospectStatusId={
					(pipelineDetail?.prospectStatus.id ??
						ProspectSteps.Bch) as unknown as ProspectSteps
				}
			/>

			{selectedPlafons.length > 0 &&
				selectedPlafons.map((plafon) => (
					<div key={plafon.prospectPlafonId}>
						<FleetTitle level={3}>Plafon {plafon.plafonType?.value}</FleetTitle>
						<DataRow
							style={{ marginBottom: 20 }}
							items={[
								[
									[
										'Outstanding Principal',
										formatCurrency(plafon.outstandingPrincipal),
									],
									[
										'Plafond Existing',
										formatCurrency(plafon.existingFinancingLimit),
									],
								],
								[
									['Plafon Diajukan', formatCurrency(plafon?.limitApplication)],
									['Detail Sumber Order', plafon.referrer?.unitName],
								],
							]}
						/>
					</div>
				))}
			<Divider />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="productOffer"
				onFinish={(values) => onSubmit(values)}
				initialValues={{
					ultimateOwner: pipelineDetail.ultimateOwner,
					currentIssue: pipelineDetail.currentIssue,
					creditScheme: pipelineDetail.creditScheme,
					approvalNotes: pipelineDetail.approvalNotes,
					pipelinePlafonTypeIds: pipelineDetail.pipelinePlafonTypeIds,
				}}
			>
				{selectedPlafons?.length > 0 && (
					<Fragment>
						<Row
							gutter={[20, 40]}
							style={{ marginBottom: 20 }}
						>
							<Col span={24}>
								<Form.Item
									name={fieldName('pipelinePlafonTypeIds')}
									label="Jenis Plafon Diajukan"
									labelCol={{ span: 5 }}
									wrapperCol={{
										style: { marginLeft: -10 },
									}}
									rules={isSavingMode ? [formRule.required()] : []}
								>
									<Checkbox.Group disabled>
										<Flex gap={20}>
											{selectedPlafons?.map((plafon) => (
												<Checkbox
													value={plafon.plafonType?.id}
													key={plafon.prospectPlafonId}
												>
													{plafon.plafonType?.value}
												</Checkbox>
											))}
										</Flex>
									</Checkbox.Group>
								</Form.Item>
							</Col>
						</Row>

						<Divider />
					</Fragment>
				)}
				<Row gutter={[20, 40]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('ultimateOwner')}
							label="Ultimate Owner"
							labelCol={{ span: 10 }}
							style={{
								marginLeft: -5,
							}}
							rules={isSavingMode ? [formRule.required()] : []}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('currentIssue')}
							label="Current Issue"
							labelCol={{ span: 5 }}
							style={{
								marginLeft: -5,
							}}
							rules={isSavingMode ? [formRule.required()] : []}
						>
							<TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('creditScheme')}
							label="Skema Kredit"
							labelCol={{ span: 5 }}
							style={{
								marginLeft: -5,
							}}
							rules={isSavingMode ? [formRule.required()] : []}
						>
							<TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('approvalNotes')}
							label="Approval Notes"
							labelCol={{ span: 5 }}
							style={{
								marginLeft: -5,
							}}
							rules={isSavingMode ? [formRule.required()] : []}
						>
							<TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row
					gutter={[20, 40]}
					align="middle"
					style={{
						marginBottom: 24,
					}}
				>
					<Col span={24}>
						<Checkbox
							onChange={(e) => {
								setIsSavingMode(e.target.checked);
								form.validateFields(); // Revalidate fields when checkbox changes
							}}
						>
							Checklist kotak disamping jika data terkait{' '}
							<strong style={{ color: '#1557BC' }}>Current Issue</strong> dan{' '}
							<strong style={{ color: '#1557BC' }}>Skema Kredit</strong> telah
							siap untuk dibahas di forum pipeline.
						</Checkbox>
					</Col>
				</Row>
				<Divider />
				<Flex
					justify="flex-end"
					gap={12}
				>
					<Button
						type="primary"
						loading={updateFBDHPipelineMutation.isPending}
						style={{
							width: 200,
							display: 'block',
							marginTop: 30,
							borderColor: '#1557BC',
							background: 'white',
							color: '#1557BC',
							fontWeight: 600,
						}}
						onClick={() => onUpdate()}
					>
						UPDATE
					</Button>
					<Button
						type="primary"
						htmlType="submit"
						loading={saveFBDHPipelineMutation.isPending}
						style={{
							width: 200,
							display: 'block',
							marginTop: 30,
						}}
						disabled={!isSavingMode}
					>
						SAVE
					</Button>
				</Flex>
			</Form>
			<Divider />
		</Fragment>
	);
};

export default FBDHPipelinePage;
